import { apiCompensationComponents } from "@/api/compensation-component.api.ts";
import useErrorHandle from "@/hooks/use-error-handle.hook.tsx";
import { ApiCompensationComponentPayload } from "@/types/compensation-component.types.ts";
import { ApiErrorException } from "@/types/api.types.ts";
import { createQueryKeys } from "@lukemorales/query-key-factory";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Control, FieldValues } from "react-hook-form";

export const compensationComponentKeys = createQueryKeys("compensationComponents", {
  all: null,
});

export const useCompensationComponents = (enabled = true) =>
  useQuery({
    ...compensationComponentKeys.all,
    enabled,
    queryFn: async () => {
      const result = await apiCompensationComponents.getAll();
      if (result.error) throw new ApiErrorException(result.error);
      return result.data;
    },
    staleTime: 60 * 1000,
  });

export interface CompensationComponentSaveParams {
  id?: number;
  payload: ApiCompensationComponentPayload;
}

export const useCompensationComponentSave = <TFieldValues extends FieldValues = FieldValues>(
  control?: Control<TFieldValues>
) => {
  const queryClient = useQueryClient();
  const { onError } = useErrorHandle();

  return useMutation({
    mutationFn: async ({ id, payload }: CompensationComponentSaveParams) => {
      const result = id
        ? await apiCompensationComponents.update(id, payload)
        : await apiCompensationComponents.create(payload);
      if (result.error || !result.data) throw new ApiErrorException(result.error);
      return result.data;
    },
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: compensationComponentKeys.all.queryKey }),
    onError: (error: Error) => onError(error, control),
  });
};

export const useCompensationComponentArchive = () => {
  const queryClient = useQueryClient();
  const { onError } = useErrorHandle();

  return useMutation({
    mutationFn: async (id: number) => {
      const result = await apiCompensationComponents.archive(id);
      if (result.error) throw new ApiErrorException(result.error);
      return result;
    },
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: compensationComponentKeys.all.queryKey }),
    onError: (error: Error) => onError(error),
  });
};

export const useCompensationComponentsSort = () => {
  const queryClient = useQueryClient();
  const { onError } = useErrorHandle();

  return useMutation({
    mutationFn: async (ids: number[]) => {
      const result = await apiCompensationComponents.updateSort(ids);
      if (result.error) throw new ApiErrorException(result.error);
      return result;
    },
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: compensationComponentKeys.all.queryKey }),
    onError: (error: Error) => onError(error),
  });
};
